'use client';

import { type ElementType, forwardRef, useId, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { FormFieldContext } from '../form-field/context/form-field.context';
import type { FormFieldContextInterface } from '../form-field/types/form-field.types';
import { FieldSetContext } from './context/field-set.context';
import { fieldSetStyles } from './styles/field-set.styles';
import type { FieldSetContextInterface, FieldSetPropsInterface } from './types/field-set.types';

/**
 * @related [`FormField`, `LabeledControl`]
 * @subcomponents [`Legend`]
 * @since 8.0.0
 */

export const FieldSet = forwardRef<HTMLFieldSetElement | HTMLDivElement, FieldSetPropsInterface>(
  (props: FieldSetPropsInterface, forwardedRef) => {
    const {
      children,
      className,
      css: cssProp,
      description,
      disabled = false,
      error = false,
      legend,
      required = false,
      optional = false,
      size,
      ...rest
    } = props;

    const styles = css(fieldSetStyles, cssProp);

    const descriptionId = useId();

    const formFieldContextValue = useMemo<FormFieldContextInterface>(
      () => ({
        error,
        disabled,
        ...(description ? { descriptionId } : {}),
        optional,
        required,
      }),
      [error, disabled, description, descriptionId, optional, required],
    );

    const fieldSetContextValue = useMemo<FieldSetContextInterface>(
      () => ({
        size,
      }),
      [size],
    );

    const Component = (legend ? 'fieldset' : 'div') as ElementType;

    /*
     * `<fieldset>` already has an implicit `role="group"`, so setting the
     * role explicitly is redundant. The `<div>` fallback
     * still needs the role to be announced as a group.
     */
    const role = legend ? undefined : 'group';

    return (
      <FormFieldContext.Provider value={formFieldContextValue}>
        <FieldSetContext.Provider value={fieldSetContextValue}>
          <Component
            ref={forwardedRef as any}
            className={cx('c11n', 'c11n_field-set', styles, className)}
            data-c11n-component="FieldSet"
            data-c11n-version={__C11N_VERSION__}
            role={role}
            {...rest}
          >
            {legend}
            {children}
            {description}
          </Component>
        </FieldSetContext.Provider>
      </FormFieldContext.Provider>
    );
  },
);

FieldSet.displayName = 'FieldSet';
